// Workspace settings → company (ADR-0047): status of the review, profile, commercial terms and credit
// of the organization. Existing workspaces without a profile complete it here; access is never blocked.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Callout, Details, Section } from "@/ui/layout";
import { CompanyForm, companyValuesOf, EMPTY_COMPANY, type CompanyValues } from "./company-form";
import { COMPANY_STATUS, countryName, ORG_TYPE_LABELS, PAYMENT_MODE_LABELS, usd } from "./labels";
import { commercialKeys, useCompany } from "./queries";

export function CompanySection({ orgId }: { orgId: string }) {
  const { data } = useCompany(orgId);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  if (!data) return null;
  const p = data.profile;
  const save = async (values: CompanyValues) => {
    await call(() => api.PUT("/orgs/{orgId}/company", { params: { path: { orgId } }, body: values }));
    await queryClient.invalidateQueries({ queryKey: commercialKeys.company(orgId) });
    setEditing(false);
    toast.success(p ? "Guardamos los datos de tu empresa." : "Registramos tu empresa. Media Interactiva la revisará.");
  };
  const status = p ? COMPANY_STATUS[p.status] : COMPANY_STATUS.NO_PROFILE;
  return (
    <Section
      title="Empresa"
      description="Datos comerciales y fiscales. Media Interactiva los valida antes de habilitar órdenes de compra o crédito."
      actions={status ? <Badge tone={status.tone}>{status.label}</Badge> : null}
    >
      {!p || editing ? (
        data.canManage ? (
          <div className="max-w-xl">
            {!p ? (
              <Callout title="Completa el perfil de tu empresa">
                Tu espacio de trabajo sigue funcionando igual. El perfil es necesario para trabajar con órdenes de
                compra.
              </Callout>
            ) : null}
            <div className="mt-4">
              <CompanyForm
                initial={p ? companyValuesOf(p) : EMPTY_COMPANY}
                legalLocked={Boolean(p) && !["PENDING_REVIEW", "REJECTED"].includes(p?.status ?? "")}
                submitLabel={p ? "Guardar cambios" : "Registrar empresa"}
                onSubmit={save}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">El administrador de tu espacio de trabajo puede completar estos datos.</p>
        )
      ) : (
        <div className="grid gap-4">
          {p.status === "REJECTED" && p.statusReason ? (
            <Callout title="Media Interactiva pidió correcciones" tone="warning">
              {p.statusReason}
            </Callout>
          ) : null}
          {!p.billingProfileComplete ? (
            <Callout title="Completa la dirección fiscal y el contacto de facturación" tone="warning">
              Media Interactiva factura desde Honduras: necesitamos la dirección fiscal de tu empresa y a quién enviar
              las facturas antes de validarla.
            </Callout>
          ) : null}
          <Details
            items={[
              { label: "Tipo", value: ORG_TYPE_LABELS[p.orgType] ?? p.orgType },
              { label: "Nombre legal", value: p.legalName },
              { label: "País", value: countryName(p.country) },
              {
                label: "Identificación tributaria",
                value: `${p.taxId}${p.taxIdFormatVerified ? "" : " (formato no verificado)"}`,
              },
              { label: "Contacto comercial", value: `${p.contactName} · ${p.contactEmail} · ${p.contactPhone}` },
              {
                label: "Dirección fiscal",
                value: p.addressLine1
                  ? [p.addressLine1, p.addressLine2, p.city, p.region, p.postalCode].filter(Boolean).join(", ")
                  : "Pendiente",
              },
              {
                label: "Contacto de facturación",
                value: p.billingContactName
                  ? [p.billingContactName, p.billingContactEmail, p.billingContactPhone].filter(Boolean).join(" · ")
                  : "Pendiente",
              },
              {
                label: "Modalidad de pago",
                value: PAYMENT_MODE_LABELS[data.terms.paymentMode] ?? data.terms.paymentMode,
              },
              {
                label: "Órdenes de compra",
                value: data.terms.purchaseOrdersEnabled ? "Habilitadas" : "No habilitadas",
              },
              {
                label: "Crédito aprobado",
                value: data.terms.creditLimitMicros > 0 ? usd(data.terms.creditLimitMicros) : "Sin crédito",
              },
              ...(data.terms.creditLimitMicros > 0
                ? [
                    { label: "Comprometido", value: usd(data.credit.committedMicros) },
                    { label: "Disponible", value: usd(data.credit.availableMicros) },
                  ]
                : []),
            ]}
          />
          {data.canManage ? (
            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(true);
                }}
              >
                Editar datos
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}
